<template>
  <div class="historical-drawer" :class="{ 'is-active': isOpen }">
    <div ref="historyScrollContainer" class="historical-drawer-content">
      <div class="history-header">
        <h3 class="history-title">
          {{ showingDeleted ? 'Recently Deleted' : 'History' }}
        </h3>
        <div class="history-header-controls">
          <template v-if="showingDeleted">
            <button class="button is-small" @click="toggleDeletedView">
              <span class="icon is-small">
                <FontAwesomeIcon :icon="faArrowLeft" />
              </span>
              <span>Back</span>
            </button>
          </template>
          <template v-else>
            <button
              v-if="store.hasAnalytics"
              class="button is-small"
              @click="openAnalyticsPanel"
            >
              <span class="icon is-small">
                <FontAwesomeIcon :icon="faChartBar" />
              </span>
              <span>Analytics</span>
            </button>
            <div v-if="!store.isFrontendOnly" class="dropdown is-right" :class="{ 'is-active': headerMenuOpen }">
              <div class="dropdown-trigger">
                <button class="button is-small" aria-haspopup="true" @click="headerMenuOpen = !headerMenuOpen">
                  <span class="icon is-small">
                    <FontAwesomeIcon :icon="faEllipsisVertical" />
                  </span>
                </button>
              </div>
              <div class="dropdown-menu" role="menu">
                <div class="dropdown-content">
                  <a class="dropdown-item" @click="toggleDeletedView(); headerMenuOpen = false">
                    <span class="icon is-small mr-2">
                      <FontAwesomeIcon :icon="faTrashCan" />
                    </span>
                    Recently Deleted
                  </a>
                </div>
              </div>
            </div>
          </template>
          <button class="button is-small" aria-label="close" @click="close">
            <span class="icon is-small">
              <FontAwesomeIcon :icon="faXmark" />
            </span>
          </button>
        </div>
      </div>
      <div v-if="!store.isFrontendOnly" class="historical-days p-4">
        <!-- Deleted Timers View -->
        <div v-if="showingDeleted" class="deleted-timers">
          <div v-if="isLoading" class="historical-timers">
            <div v-for="i in 3" :key="i" class="deleted-timer py-2">
              <div class="columns is-mobile is-gapless mb-0">
                <div class="column">
                  <div class="skeleton-lines"><div></div><div></div><div></div></div>
                </div>
                <div class="column is-narrow ml-4">
                  <div class="skeleton-lines"><div></div></div>
                </div>
                <div class="column is-narrow ml-2">
                  <div class="skeleton-lines"><div></div></div>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="store.deletedBlocks.length === 0" class="has-text-centered my-6">
            <p class="is-size-6">No recently deleted timers</p>
            <p class="is-size-7 has-text-grey mt-2">Deleted timers will appear here.</p>
          </div>
          <div v-else class="historical-timers">
            <div 
              v-for="timer in store.deletedBlocks" 
              :key="timer.id"
              class="deleted-timer py-2"
            >
              <div class="columns is-mobile is-gapless mb-0">
                <div class="column">
                  <p class="is-size-6">{{ timer.title }}</p>
                  <p class="is-size-7 has-text-grey">
                    {{ formatTime(timer.startDate) }} - {{ formatTime(timer.endDate) }}
                  </p>
                  <p class="is-size-7 has-text-grey">
                    {{ formatFullDate(new Date(timer.startDate)) }}
                  </p>
                </div>
                <div class="column is-narrow">
                  <p class="has-text-grey ml-4">{{ formatDuration(timer.duration) }}</p>
                </div>
                <div class="column is-narrow">
                  <button 
                    @click="restoreTimer(timer.id)"
                    class="button is-small is-ghost"
                    title="Restore timer"
                  >
                    <span class="icon is-small">
                      <FontAwesomeIcon :icon="faUndo" />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Regular History View -->
        <div v-else>
          <div v-if="isLoading && historyDays.length === 0">
            <div v-for="i in 3" :key="i" class="mb-6">
              <p class="title is-6 has-skeleton mb-3">Loading</p>
              <div class="historical-timers">
                <div v-for="j in 2" :key="j" class="historical-entry py-2">
                  <div class="columns is-mobile is-gapless mb-0">
                    <div class="column">
                      <div class="skeleton-lines"><div></div><div></div></div>
                    </div>
                    <div class="column is-narrow ml-4">
                      <div class="skeleton-lines"><div></div></div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="has-text-right mt-3">
                <div class="skeleton-lines"><div></div><div></div></div>
              </div>
            </div>
          </div>
          <div v-else-if="historyDays.length === 0" class="has-text-centered my-6">
            <p class="is-size-6">{{ store.hasUnlimitedHistory ? 'No history yet.' : 'No history found in the last 7 days.' }}</p>
            <p v-if="!store.hasUnlimitedHistory" class="is-size-7 has-text-grey mt-2">Timers and contexts from the last 7 days will appear here.</p>
          </div>
          <div v-else v-for="day in historyDays" :key="day.date" class="mb-6">
            <p class="title is-6 has-text-weight-medium mb-3">{{ formatFullDate(new Date(day.date)) }}</p>
            <div class="historical-timers">
              <div
                v-for="row in day.rows"
                :key="row.key"
                class="history-row"
                :class="{
                  'history-row--context': row.type === 'context-header',
                  'history-row--context-timer': row.type === 'context-timer',
                }"
              >
                <!-- Context header -->
                <template v-if="row.type === 'context-header'">
                  <div class="columns is-mobile is-gapless mb-0">
                    <div class="column">
                      <p class="is-size-7 history-row__context-label">{{ row.context.display_label }}</p>
                      <p class="is-size-7 has-text-grey">
                        {{ formatHistoryRange(row.context.day_started_at || row.context.started_at, row.context.day_stopped_at || row.context.stopped_at) }}
                      </p>
                    </div>
                    <div class="column is-narrow">
                      <p class="is-size-7 has-text-grey ml-4">{{ formatDuration(row.context.day_duration || row.context.duration_seconds || 0) }}</p>
                    </div>
                  </div>
                </template>

                <!-- Timer (context child or standalone) -->
                <template v-else>
                  <div class="columns is-mobile is-gapless mb-0">
                    <div class="column">
                      <p class="is-size-7" style="font-weight: 500">{{ row.timer.title }}</p>
                      <p v-if="timerProjectTags(row.timer)" class="is-size-7 has-text-grey history-row__secondary">
                        {{ timerProjectTags(row.timer) }}
                      </p>
                      <p class="is-size-7 has-text-grey">
                        {{ formatHistoryRange(row.timer.started_at, row.timer.ended_at) }}
                      </p>
                    </div>
                    <div class="column is-narrow">
                      <p class="is-size-7 has-text-grey ml-4">{{ formatDuration(row.timer.duration || 0) }}</p>
                    </div>
                  </div>
                </template>
              </div>
            </div>
            <div class="has-text-right mt-3">
              <p class="is-size-7 has-text-grey-light">
                Tracked: <span class="has-text-weight-medium">{{ formatDuration(day.total_duration) }}</span>
              </p>
              <p v-if="store.hasContextTimer" class="is-size-7 has-text-grey-light">
                Context: <span class="has-text-weight-medium">{{ formatDuration(day.context_total_duration || 0) }}</span>
              </p>
            </div>
          </div>

          <!-- Pagination footer -->
          <div v-if="historyDays.length > 0" class="mt-5">
            <!-- Paid: load more button -->
            <template v-if="store.hasUnlimitedHistory">
              <div v-if="isLoadingMore" class="mb-6">
                <p class="title is-6 has-skeleton mb-3">Loading</p>
                <div class="historical-timers">
                  <div v-for="j in 2" :key="j" class="historical-entry py-2">
                    <div class="columns is-mobile is-gapless mb-0">
                      <div class="column">
                        <div class="skeleton-lines"><div></div><div></div></div>
                      </div>
                      <div class="column is-narrow ml-4">
                        <div class="skeleton-lines"><div></div></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <button v-else-if="hasMore && !canAutoLoadMore" class="button is-small is-fullwidth" @click="loadMore()">
                Load more
              </button>
              <div v-else-if="hasMore" ref="loadMoreSentinel" class="load-more-sentinel"></div>
              <p v-else class="is-size-7 has-text-grey-light has-text-centered">You've reached the start of your history.</p>
            </template>

            <!-- Free: upgrade prompt at the boundary -->
            <template v-else>
              <div class="history-upgrade-prompt">
                <p class="is-size-7 has-text-grey-light has-text-centered mb-2">Showing the last 7 days</p>
                <div class="notification is-info is-light p-3">
                  <p class="is-size-7">
                    <strong>Unlock unlimited history</strong> with Timist Plus.
                  </p>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
      <div v-else class="p-4">
        <p class="has-text-grey">History is only available with an account.</p>
      </div>
    </div>
    <div class="historical-drawer-background" @click="close"></div>
  </div>
</template>

<script setup>
import { computed, ref, watch, inject, onUnmounted } from 'vue';
import { useBlocksStore } from '../stores/blocks';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faXmark, faUndo, faChartBar, faEllipsisVertical, faTrashCan, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { formatDuration, formatFullDate, formatTime } from '../utils/time';
import axios from 'axios';

const store = useBlocksStore();
const openAnalyticsPanel = inject('openAnalytics', null);

const props = defineProps({
  isOpen: Boolean
});

const emit = defineEmits(['close']);

const days = ref([]);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const hasMore = ref(false);
const nextBefore = ref(null);
const showingDeleted = ref(false);
const headerMenuOpen = ref(false);
const historyScrollContainer = ref(null);
const loadMoreSentinel = ref(null);
const canAutoLoadMore = typeof IntersectionObserver !== 'undefined';
let sentinelObserver = null;

const historyDays = computed(() => {
  return days.value
    .map((day) => {
      const contexts = Array.isArray(day.contexts) ? day.contexts.map((context) => ({ ...context, timers: [] })) : [];
      const sortedContexts = contexts
        .slice()
        .sort((a, b) => new Date(b.day_started_at || b.started_at) - new Date(a.day_started_at || a.started_at));
      const standaloneTimers = [];
      const sortedTimers = [...(day.timers || [])].sort((a, b) => new Date(a.started_at) - new Date(b.started_at));

      sortedTimers.forEach((timer) => {
        const timerStartsAt = new Date(timer.started_at).getTime();
        const matchingContext = sortedContexts.find((context) => {
          const contextStartsAt = new Date(context.day_started_at || context.started_at).getTime();
          const contextStopsAt = new Date(context.day_stopped_at || context.stopped_at || Date.now()).getTime();
          return timerStartsAt >= contextStartsAt && timerStartsAt < contextStopsAt;
        });

        if (matchingContext) {
          matchingContext.timers.push(timer);
        } else {
          standaloneTimers.push(timer);
        }
      });

      const grouped = [
        ...sortedContexts.map((context) => ({
          type: 'context',
          sortAt: new Date(context.day_started_at || context.started_at).getTime(),
          context,
          timers: context.timers.sort((a, b) => new Date(a.started_at) - new Date(b.started_at)),
        })),
        ...standaloneTimers.map((timer) => ({
          type: 'timer',
          sortAt: new Date(timer.started_at).getTime(),
          timer,
        })),
      ].sort((a, b) => b.sortAt - a.sortAt);

      // Flatten into individual rows
      const rows = [];
      grouped.forEach((item) => {
        if (item.type === 'context') {
          rows.push({ type: 'context-header', context: item.context, key: `ctx-${item.context.id}` });
          item.timers.forEach((timer) => {
            rows.push({ type: 'context-timer', timer, key: `ctx-t-${timer.id}` });
          });
        } else {
          rows.push({ type: 'timer', timer: item.timer, key: `t-${item.timer.id}` });
        }
      });

      return {
        ...day,
        rows,
        context_total_duration: day.context_total_duration || 0,
      };
    })
    .filter((day) => day.rows.length > 0);
});

const close = () => {
  emit('close');
};

const toggleDeletedView = () => {
  showingDeleted.value = !showingDeleted.value;
  if (showingDeleted.value) {
    store.fetchDeletedTimers();
  }
};

const restoreTimer = (id) => {
  store.restoreTimer(id);
};

const formatHistoryRange = (startedAt, endedAt) => {
  const startLabel = formatTime(new Date(startedAt));
  const endLabel = endedAt ? formatTime(new Date(endedAt)) : 'Now';
  return `${startLabel} - ${endLabel}`;
};

const timerProjectTags = (timer) => {
  const tokens = [];

  if (timer.project?.name) {
    tokens.push(`@${timer.project.name}`);
  }

  (timer.tags || []).forEach((tag) => {
    tokens.push(`#${tag.name}`);
  });

  return tokens.join(' ');
};

const loadHistoricalTimers = async ({ reset = true } = {}) => {
  if (store.isFrontendOnly) return;

  if (reset) {
    days.value = [];
    nextBefore.value = null;
    hasMore.value = false;
    isLoading.value = true;
  } else {
    isLoadingMore.value = true;
  }

  try {
    const params = {};
    if (!reset && nextBefore.value) params.before = nextBefore.value;
    const { data } = await axios.get('/api/timers/historical', { params });
    days.value = reset ? data.days : [...days.value, ...data.days];
    hasMore.value = data.has_more;
    nextBefore.value = data.next_before;
  } catch (error) {
    console.error('Error loading historical timers:', error);
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
  }
};

const loadMore = () => {
  if (!isLoadingMore.value && hasMore.value) {
    loadHistoricalTimers({ reset: false });
  }
};

watch(loadMoreSentinel, (el) => {
  sentinelObserver?.disconnect();
  if (el && canAutoLoadMore && store.hasUnlimitedHistory && hasMore.value) {
    sentinelObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          loadMore();
        }
      },
      { root: historyScrollContainer.value, threshold: 0.1 }
    );
    sentinelObserver.observe(el);
  }
});

onUnmounted(() => sentinelObserver?.disconnect());

// Load historical timers every time the drawer is opened
watch(() => props.isOpen, (newValue) => {
  if (newValue && !store.isFrontendOnly) {
    loadHistoricalTimers({ reset: true });
  }
});
</script>

<style scoped>
.historical-drawer {
  display: contents;
}

.historical-drawer-content {
  position: fixed;
  top: 0;
  left: -300px;
  width: 300px;
  height: 100%;
  background: var(--bulma-scheme-main);
  box-shadow: 2px 0 5px rgba(0,0,0,0.1);
  transition: transform 0.3s ease-out;
  overflow-y: auto;
  z-index: 41;
  visibility: hidden;
  pointer-events: none;
}

.historical-drawer.is-active .historical-drawer-content {
  transform: translateX(300px);
  visibility: visible;
  pointer-events: auto;
}

/* Header — matches Analytics header */
.history-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--bulma-scheme-main);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--bulma-scheme-main-ter);
}
.history-title {
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
  color: var(--bulma-text);
}
.history-header-controls {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.historical-drawer-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.3);
  opacity: 0;
  transition: opacity 0.3s ease-out;
  z-index: 38;
  visibility: hidden;
  pointer-events: none;
}

.historical-drawer.is-active .historical-drawer-background {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.historical-timers {
  background-color: var(--bulma-scheme-main-bis);
  border-radius: var(--bulma-radius);
  overflow: hidden;
}

/* Every row is a direct child, fills the full cell */
.history-row {
  padding: 0.5rem;
  padding-left: 0.75rem;
  border-bottom: 1px solid var(--bulma-scheme-main-ter);
}

.history-row:last-child {
  border-bottom: none;
}

/* Context header row: orange bg */
.history-row--context {
  background: rgba(213, 99, 45, 0.045);
  position: relative;
}

/* No border gap between context header and its timers, or between sibling context-timers */
.history-row--context:has(+ .history-row--context-timer) {
  border-bottom: none;
}

.history-row--context-timer:has(+ .history-row--context-timer) {
  border-bottom: none;
}

/* Gap between consecutive context headers (no child timers in between) */
.history-row--context + .history-row--context {
  margin-top: 2px;
}

/* Timer under a context: extra indent */
.history-row--context-timer {
  padding-left: 1.25rem;
  position: relative;
}

/* Orange bar via pseudo-element for inset control */
.history-row--context::before,
.history-row--context-timer::before {
  content: '';
  position: absolute;
  left: 0.25rem;
  top: 0;
  bottom: 0;
  width: 2px;
  background: rgba(213, 99, 45, 0.5);
}

/* Top inset on context header */
.history-row--context::before {
  top: 0.35rem;
  border-radius: 1px 1px 0 0;
}

/* Bottom inset: context header with no child timers following */
.history-row--context:not(:has(+ .history-row--context-timer))::before {
  bottom: 0.35rem;
  border-radius: 1px;
}

/* Bottom inset: last context-timer in a run */
.history-row--context-timer:not(:has(+ .history-row--context-timer))::before {
  bottom: 0.35rem;
  border-radius: 0 0 1px 1px;
}

.history-row__context-label {
  color: #d5632d;
  font-weight: 500;
}

.history-row__secondary {
  line-height: 1.3;
}

.load-more-sentinel {
  height: 1px;
}

.history-upgrade-prompt {
  padding-bottom: 0.5rem;
}

.deleted-timer {
  border-bottom: 1px solid var(--bulma-scheme-main-ter);
  opacity: 0.7;
}

.deleted-timer:last-child {
  border-bottom: none;
}

.deleted-timer:hover {
  opacity: 1;
}

/* Mobile: full-width drawer */
@media (max-width: 768px) {
  .historical-drawer-content {
    left: -100vw;
    width: 100vw;
    max-width: 100vw;
  }

  .historical-drawer.is-active .historical-drawer-content {
    transform: translateX(100vw);
  }
}
</style>
